import React, {useContext} from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from '../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../i18n/constants/keys';
import { updateJobApplicantStatus } from '../../../../../utils/api_service/JobOpenings';
import { SegmentType } from '../../../../../utils/types/JobOpenings';
import { RootState } from '../../../../../utils/redux_store/store';
import {JobOpeningsContext} from "../../../../../utils/contexts/JobOpening";
import {
  AddRounded,
  BookmarkOutlined,
  CancelOutlined,
  ChevronRightRounded,
  CheckCircleOutlined,
  ContentPasteOutlined,
  InfoOutlined,
  LightbulbOutlined,
  StarOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';

type AnalyticActionProps = {
  actionSelected: string[];
  setActionSelected: (value: string[]) => void;
  setSegmentedData: (value: SegmentType[][]) => void;
  defaultSegment: SegmentType[][];
  loading: boolean;
  allCandidates: any[]; // All candidates data
  setIsProfileModalOpen: (value: boolean) => void;
  setProfileModalCandidates: (value: any[]) => void;
  setProfileModalInitialIndex: (value: number) => void;
  setProfileModalTileName: (value: string) => void;
  handleCandidateClickFromChart?: (
    candidate: any,
    tileCandidates?: any[],
    tileName?: string
  ) => void;
};

export const AnalyticAction = ({
  actionSelected,
  setActionSelected,
  setSegmentedData,
  defaultSegment,
  loading,
  allCandidates,
  setIsProfileModalOpen,
  setProfileModalCandidates,
  setProfileModalInitialIndex,
  setProfileModalTileName,
  handleCandidateClickFromChart,
}: AnalyticActionProps) => {
  const { t } = useTranslation(NS.adminPages);
  const AA = ADMIN_PAGES_KEYS.jobOpenings.applicantAnalysis;
  const { id } = useParams<{ id: string }>();


  const { jobOpeningElements } = useContext(JobOpeningsContext);
  const { subscriptions } = useSelector((state: RootState) => state.subscriptions);

  const editApplicants = async (requestObject: {
    jobApplicantIds: string[];
    applicantStatus: string;
  }) => {
    try {
      const response = await updateJobApplicantStatus(id, requestObject);
      if (!response.ok) {
        toast.error('Error Changing applicant status', {
          position: 'bottom-center',
        });
        return;
      }
      setActionSelected([]);
      setSegmentedData(defaultSegment);
    } catch (error) {
      toast.error('Error Changing applicant status', {
        position: 'bottom-center',
      });
    }
  };

  const handleSelectAll = () => {
    const allCandidateIds = allCandidates.map(
      (candidate) => candidate.userId || candidate.id || candidate._id,
    );

    const areAllSelected = allCandidateIds.length > 0 &&
      allCandidateIds.every(id => actionSelected.includes(id));

    if (areAllSelected) {
      setActionSelected([]);
      toast.success(t(AA.analyticAction.deselectAll), {
        position: 'bottom-center',
      });
    } else {
      setActionSelected(allCandidateIds);
      toast.success(t(AA.analyticAction.selectAll), {
        position: 'bottom-center',
      });
    }
  };

  const handleOpenInTriage = () => {
    let candidatesToOpen = [];
    let candidateIds = [];

    if (actionSelected.length > 0) {
      candidateIds = actionSelected;
      candidatesToOpen = allCandidates.filter((candidate) =>
        actionSelected.includes(
          candidate.userId || candidate.id || candidate._id,
        ),
      );
    } else {
      candidateIds = allCandidates.map(
        (candidate) => candidate.userId || candidate.id || candidate._id,
      );
      candidatesToOpen = allCandidates;
      setActionSelected(candidateIds);
    }

    if (candidatesToOpen.length === 0) {
      toast.error(t(AA.analyticAction.noCandidates), { position: 'bottom-center' });
      return;
    }

    if (handleCandidateClickFromChart && candidatesToOpen.length > 0) {
      handleCandidateClickFromChart(
        candidatesToOpen[0],
        candidatesToOpen,
        'Selected Candidates Triage',
      );
    } else {
      try {
        if (typeof setProfileModalCandidates === 'function') {
          setProfileModalCandidates(candidatesToOpen);
        }
        if (typeof setProfileModalInitialIndex === 'function') {
          setProfileModalInitialIndex(0);
        }
        if (typeof setProfileModalTileName === 'function') {
          setProfileModalTileName('Selected Candidates Triage');
        }
        if (typeof setIsProfileModalOpen === 'function') {
          setIsProfileModalOpen(true);
        }
      } catch (error) {
        //
      }
    }

    toast.success(t(AA.analyticAction.openInTriage), { position: 'bottom-center' });
  };

  const handleOptionClick = (optionValue: string) => {
    if (optionValue === 'Open-in-Triage') {
      handleOpenInTriage();
    } else if (optionValue === 'Select-All') {
      handleSelectAll();
    } else {
      if (actionSelected.length > 0) {
        const requestObject = {
          jobApplicantIds: actionSelected,
          applicantStatus: optionValue,
        };
        editApplicants(requestObject).then(() =>
          toast.success(`Candidates moved to ${optionValue}!`, {
            position: 'bottom-center',
          }),
        );
      } else {
        toast.error(t(AA.analyticAction.noSelection), { position: 'bottom-center' });
      }
    }
  };

  const statusList = [
    {
      value: 'New',
      label: t(AA.analyticAction.moveToNew),
    },
    {
      value: 'Suitable',
      label: t(AA.analyticAction.moveToSuitable),
    },
    {
      value: 'Auto-Assessment',
      label: t(AA.analyticAction.moveToAutoAssess),
    },
    {
      value: 'Outstanding',
      label: t(AA.analyticAction.moveToOutstanding),
    },
    {
      value: 'Shortlisted',
      label: t(AA.analyticAction.moveToShortList),
    },
    {
      value: 'Unsuccessful-Pending',
      label: t(AA.analyticAction.moveToUnsuccessful),
    },
    ...(jobOpeningElements?.includes('Psychometric')
        ? [
          {
            value: 'Psychometric',
            label: 'Move selected to Psychometric',
          },
        ]
        : []),
    ...(jobOpeningElements?.includes('Wellness')
        ? [
          {
            value: 'Wellness',
            label: 'Move selected to Wellness',
          },
        ]
        : []),
  ];

  const getIcon = (value: string) => {
    switch (value) {
      case 'New':
        return <AddRounded sx={{ fontSize: 16, color: '#0ea5e9' }} />;
      case 'Suitable':
        return <CheckCircleOutlined sx={{ fontSize: 16, color: '#22c55e' }} />;
      case 'Auto-Assessment':
        return <ContentPasteOutlined sx={{ fontSize: 16, color: '#8b5cf6' }} />;
      case 'Outstanding':
        return <StarOutlined sx={{ fontSize: 16, color: '#f59e0b' }} />;
      case 'Shortlisted':
        return <BookmarkOutlined sx={{ fontSize: 16, color: '#6366f1' }} />;
      case 'Unsuccessful-Pending':
        return <CancelOutlined sx={{ fontSize: 16, color: '#f43f5e' }} />;
      case 'Psychometric':
      case 'Wellness':
        return <LightbulbOutlined sx={{ fontSize: 16, color: '#a855f7' }} />;
      default:
        return <ContentPasteOutlined sx={{ fontSize: 16, color: '#64748b' }} />;
    }
  };

  const isAllSelected = allCandidates.length > 0 && allCandidates.every(id => actionSelected.includes(id));

  return (
    <Stack spacing={3} sx={{ width: '100%' }}>
      {/* Primary Control Actions */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
        <Button
          onClick={handleSelectAll}
          disabled={loading || allCandidates.length === 0}
          variant={isAllSelected ? 'outlined' : 'contained'}
          startIcon={
            isAllSelected
              ? <CheckCircleOutlined sx={{ fontSize: 14 }} />
              : <ContentPasteOutlined sx={{ fontSize: 14 }} />
          }
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '12px',
            py: 1.25,
            textTransform: 'none',
            ...(isAllSelected
              ? {
                  bgcolor: '#eff6ff',
                  borderColor: '#bfdbfe',
                  color: '#1d4ed8',
                  '&:hover': { bgcolor: '#dbeafe' },
                }
              : {
                  bgcolor: '#2563eb',
                  borderColor: '#2563eb',
                  color: 'white',
                  '&:hover': { bgcolor: '#1d4ed8' },
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                }),
            '&:disabled': { opacity: 0.5 },
          }}
        >
          <Typography component="span" noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {isAllSelected ? t(AA.analyticAction.deselectAll) : t(AA.analyticAction.selectAll)}
          </Typography>
        </Button>

        <Button
          onClick={handleOpenInTriage}
          disabled={loading || allCandidates.length === 0}
          variant="contained"
          startIcon={<VisibilityOutlined sx={{ fontSize: 14 }} />}
          sx={{
            fontSize: '12px',
            fontWeight: 600,
            borderRadius: '12px',
            py: 1.25,
            textTransform: 'none',
            bgcolor: '#059669',
            border: '1px solid',
            borderColor: '#059669',
            color: 'white',
            '&:hover': { bgcolor: '#047857' },
            boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
            '&:disabled': { opacity: 0.5 },
          }}
        >
          <Typography component="span" noWrap sx={{ fontSize: 'inherit', fontWeight: 'inherit' }}>
            {t(AA.analyticAction.openInTriage)}
          </Typography>
        </Button>
      </Box>

      {/* Move Status List */}
      <Box>
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 700,
            color: '#94a3b8',
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            mb: 1,
            px: 0.5,
          }}
        >
          Change Status
        </Typography>
        <Stack spacing={0.75} sx={loading ? { animation: 'pulse 2s infinite', '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.5 } } } : {}}>
          {loading
            ? Array.from({ length: 6 }).map((_, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  height={44}
                  sx={{ borderRadius: '12px' }}
                />
              ))
            : statusList.map((status, index) => (
                <Button
                  key={index}
                  onClick={() => handleOptionClick(status.value)}
                  disabled={loading}
                  fullWidth
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    textAlign: 'left',
                    borderRadius: '12px',
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: '#f1f5f9',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    textTransform: 'none',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: '#a7f3d0',
                      bgcolor: 'rgba(236,253,245,0.2)',
                      '& .status-arrow': {
                        color: '#10b981',
                        transform: 'translateX(2px)',
                      },
                      '& .status-icon-box': {
                        bgcolor: 'white',
                      },
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
                    <Box
                      className="status-icon-box"
                      sx={{
                        flexShrink: 0,
                        width: 28,
                        height: 28,
                        borderRadius: '8px',
                        bgcolor: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        border: '1px solid',
                        borderColor: 'rgba(241,245,249,0.5)',
                        transition: 'background-color 0.2s',
                      }}
                    >
                      {getIcon(status.value)}
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#334155',
                        transition: 'color 0.2s',
                      }}
                    >
                      {status.label}
                    </Typography>
                  </Stack>
                  <ChevronRightRounded
                    className="status-arrow"
                    sx={{
                      fontSize: 16,
                      color: '#cbd5e1',
                      flexShrink: 0,
                      transition: 'all 0.2s',
                    }}
                  />
                </Button>
              ))}
        </Stack>
      </Box>

      {/* Instructions Card */}
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          gap: 1.5,
          bgcolor: 'rgba(255,251,235,0.5)',
          border: '1px solid',
          borderColor: '#fef3c7',
          borderRadius: '16px',
          p: 2,
          color: '#92400e',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            width: 24,
            height: 24,
            borderRadius: '50%',
            bgcolor: '#fef3c7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#d97706',
          }}
        >
          <InfoOutlined sx={{ fontSize: 14 }} />
        </Box>
        <Box sx={{ flex: 1, fontSize: '12px', lineHeight: 1.6, color: '#78350f' }}>
          <Typography sx={{ fontWeight: 700, display: 'block', mb: 0.5, color: '#451a03', fontSize: 'inherit' }}>
            {t(AA.analyticAction.instructionsLabel)}
          </Typography>
          Click on a box in the grid containing candidates, then select the status you wish to move them to. Use &ldquo;Select All&rdquo; to select all candidates or &ldquo;Open in Triage&rdquo; to view profile cards.
        </Box>
      </Paper>
    </Stack>
  );
};
